import React, { useCallback, useEffect, useState } from 'react';
import {
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  Badge,
  Flex,
  useColorModeValue,
  Box,
} from '@chakra-ui/react';
import { FiBell } from 'react-icons/fi';
import { useTranslation } from 'i18n/TranslationProvider';
import { useNavigate } from 'react-router-dom';
import API from 'api/api';
import { useGetUserRole } from 'domains/user/hooks/useGetUserRole';

export default function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useGetUserRole();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const menuBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
  );
  const headerBorderColor = useColorModeValue('gray.100', 'gray.700');
  const unreadNotificationBg = useColorModeValue('blue.50', 'blue.900');
  const itemHoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const itemBorderColor = useColorModeValue('gray.50', 'gray.700');

  const fetchNotifications = useCallback(async () => {
    try {
      if (role !== 'admin') return; // Only admins have this specific API for now in our usecase
      const resp = await API.get('/notifications');
      const data = resp.data?.data || [];
      setNotifications(data);
      setUnreadCount(data.filter((n) => !n.read).length);
    } catch (e) {
      console.error(e);
    }
  }, [role]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Poll every minute
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try {
        await API.put(`/notifications/${notification._id}/read`);
        fetchNotifications();
      } catch (e) {
        console.error(e);
      }
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  if (role !== 'admin') return null;

  return (
    <Menu>
      <MenuButton p="0px" position="relative">
        <IconButton
          icon={<FiBell />}
          variant="ghost"
          color="gray.500"
          aria-label={t('Notifications')}
          borderRadius="full"
        />
        {unreadCount > 0 && (
          <Badge
            position="absolute"
            top="4px"
            right="4px"
            colorScheme="red"
            borderRadius="full"
            fontSize="0.6em"
            px={1.5}
          >
            {unreadCount}
          </Badge>
        )}
      </MenuButton>
      <MenuList
        boxShadow={shadow}
        p="0px"
        mt="10px"
        borderRadius="20px"
        bg={menuBg}
        border="none"
        w="max-content"
        maxW="350px"
        maxH="400px"
        overflowY="auto"
      >
        <Flex w="100%" px={4} py={3} borderBottom="1px solid" borderColor={headerBorderColor}>
          <Text fontWeight="bold" color={textColor}>
            {t('Notifications')}
          </Text>
        </Flex>
        {notifications.length === 0 ? (
          <Box p={4} textAlign="center">
            <Text fontSize="sm" color="gray.500">
              {t('No new notifications')}
            </Text>
          </Box>
        ) : (
          notifications.map((n) => (
            <MenuItem
              key={n._id}
              onClick={() => handleNotificationClick(n)}
              bg={n.read ? 'transparent' : unreadNotificationBg}
              _hover={{ bg: itemHoverBg }}
              px={4}
              py={3}
              borderBottom="1px solid"
              borderColor={itemBorderColor}
            >
              <Flex direction="column">
                <Text fontSize="sm" color={textColor} fontWeight={n.read ? 'normal' : 'bold'}>
                  {n.message}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {new Date(n.createdAt).toLocaleString()}
                </Text>
              </Flex>
            </MenuItem>
          ))
        )}
      </MenuList>
    </Menu>
  );
}
